/**
 * Popover — reusable open/close/focus-trap/escape/outside-click behavior.
 *
 * Consumers import initPopover() and pass their popover + toggle elements.
 * Positioning is NOT handled here — use the onOpen callback or CSS.
 *
 * On mobile (<=960px), popovers automatically open in modal mode:
 *  - Popover moves to <body> to escape hidden ancestors.
 *  - A dark backdrop overlay is shown.
 *  - CSS handles fixed/centered positioning via .is-modal class.
 *
 * WCAG 2.1 AA:
 *  - Popover uses role="dialog" with aria-label (set in PHP).
 *  - Toggle has aria-expanded + aria-controls (set by consumer).
 *  - Focus moves to first focusable element on open.
 *  - Focus returns to toggle on close (Escape / close button).
 *  - Focus is trapped inside the popover while open.
 *  - Escape closes the popover.
 *  - Click outside closes the popover.
 */

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const MOBILE_NAV_BP = 960;

// Shared backdrop — created once, reused by all popovers.
let backdrop = null;

function getOrCreateBackdrop() {
  if (!backdrop) {
    backdrop = document.createElement('div');
    backdrop.className = 'popover-backdrop';
    backdrop.hidden = true;
    document.body.appendChild(backdrop);
  }
  return backdrop;
}

function isMobile() {
  return window.innerWidth <= MOBILE_NAV_BP;
}

// Elements matching the selector may still be unreachable by Tab (e.g. inside
// a display:none ancestor). offsetParent is null in that case — filter them
// out so the focus trap's first/last references always match reality.
//
// For radio groups we keep only the group's "effective tab stop" — the
// checked radio if one exists, otherwise the first radio in the group in
// DOM order — to match the browser's native single-tab-stop behavior.
function visibleFocusable(root) {
  return [...root.querySelectorAll(FOCUSABLE)].filter((el) => {
    if (el.offsetParent === null) return false;

    if (el instanceof HTMLInputElement && el.type === 'radio' && el.name) {
      const groupSelector = `input[type="radio"][name="${CSS.escape(el.name)}"]`;
      const effective =
        root.querySelector(`${groupSelector}:checked`) ||
        root.querySelector(groupSelector);
      return el === effective;
    }

    return true;
  });
}

/**
 * Initialize popover behavior on a popover/toggle pair.
 *
 * @param {Object}                opts
 * @param {HTMLElement}           opts.popover       The popover element (must have .popover-close inside).
 * @param {HTMLElement}           opts.toggle        The toggle button element.
 * @param {Function}             [opts.onOpen]       Called after the popover opens (e.g. for positioning).
 * @param {Function}             [opts.onClose]      Called after the popover closes.
 * @param {string|HTMLElement}   [opts.initialFocus] Element or selector to focus on open. Falls back to first focusable.
 * @returns {{ open: Function, close: Function, destroy: Function }}
 */
export function initPopover({ popover, toggle, onOpen, onClose, initialFocus }) {
  if (!popover || !toggle) return { open() {}, close() {}, destroy() {} };

  const closeBtn = popover.querySelector('.popover-close');

  // Modal mode state.
  let originalParent = null;
  let originalNextSibling = null;

  function enterModalMode() {
    originalParent = popover.parentNode;
    originalNextSibling = popover.nextSibling;
    document.body.appendChild(popover);
    popover.classList.add('is-modal');

    const bg = getOrCreateBackdrop();
    bg.hidden = false;
  }

  function exitModalMode() {
    popover.classList.remove('is-modal');

    const bg = getOrCreateBackdrop();
    bg.hidden = true;

    // Move popover back to its original DOM location.
    if (originalParent) {
      if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
        originalParent.insertBefore(popover, originalNextSibling);
      } else {
        originalParent.appendChild(popover);
      }
    }
    originalParent = null;
    originalNextSibling = null;
  }

  function open() {
    if (isMobile()) {
      enterModalMode();
    }

    popover.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');

    if (onOpen) onOpen();

    // Focus the caller's chosen element, or fall back to the first focusable.
    let target = null;
    if (typeof initialFocus === 'string') {
      target = popover.querySelector(initialFocus);
    } else if (initialFocus instanceof HTMLElement) {
      target = initialFocus;
    }
    if (!target) {
      [target] = visibleFocusable(popover);
    }
    if (target) target.focus();
  }

  function close(returnFocus = true) {
    const wasModal = popover.classList.contains('is-modal');

    popover.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');

    if (wasModal) {
      exitModalMode();
    }

    // Return focus to the toggle only if it's visible.
    if (returnFocus && toggle.offsetParent !== null) {
      toggle.focus();
    }

    if (onClose) onClose();
  }

  // Toggle click.
  function handleToggleClick() {
    if (popover.hidden) {
      open();
    } else {
      close(true);
    }
  }

  // Close button.
  function handleCloseClick() {
    close(true);
  }

  // Outside click.
  function handleOutsideClick(e) {
    if (
      !popover.hidden &&
      !popover.contains(e.target) &&
      e.target !== toggle &&
      !toggle.contains(e.target)
    ) {
      // Clicking the backdrop counts as outside click.
      close(false);
    }
  }

  // Escape key.
  function handleKeydown(e) {
    if (e.key === 'Escape' && !popover.hidden) {
      close(true);
    }
  }

  // Focus trap.
  function handleFocusTrap(e) {
    if (e.key !== 'Tab') return;

    const focusable = visibleFocusable(popover);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // Bind events.
  toggle.addEventListener('click', handleToggleClick);
  if (closeBtn) closeBtn.addEventListener('click', handleCloseClick);
  document.addEventListener('click', handleOutsideClick);
  document.addEventListener('keydown', handleKeydown);
  popover.addEventListener('keydown', handleFocusTrap);

  // Store instance on the DOM element for external access (e.g. mobile nav).
  popover.__popoverInstance = { open, close };

  // Cleanup.
  function destroy() {
    toggle.removeEventListener('click', handleToggleClick);
    if (closeBtn) closeBtn.removeEventListener('click', handleCloseClick);
    document.removeEventListener('click', handleOutsideClick);
    document.removeEventListener('keydown', handleKeydown);
    popover.removeEventListener('keydown', handleFocusTrap);
    delete popover.__popoverInstance;
  }

  return { open, close, destroy };
}
